import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IEmailABTest, TEmailABTestVariation } from '@feathr/blackbox';
import { EEmailABTestStatus } from '@feathr/blackbox';
import { CardV2 as Card, List, Text } from '@feathr/components';
import { getEmailABTestVersionLabel } from '@feathr/extender/utils/emailABTest';

import ABTestPill from './ABTestPill';
import type { IABTestVersionSummary } from './ABTestReport.types';
import {
  AB_TEST_METRICS,
  formatABTestRate,
  getABTestMetricLabel,
  getABTestMetricPillStatus,
  getABTestVersionPillStatus,
} from './ABTestReport.utils';

import * as styles from './ABTestSummaryVersion.css';

interface IProps {
  abTest: IEmailABTest;
  summary: IABTestVersionSummary;
  variation: TEmailABTestVariation;
}

function ABTestSummaryVersion({ abTest, summary, variation }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const metricPillStatus = getABTestMetricPillStatus(variation, abTest);
  const isFallback =
    getABTestVersionPillStatus(variation, abTest) === EEmailABTestStatus.ResolvedFallback;

  return (
    <Card width={'full'}>
      <Card.Header title={getEmailABTestVersionLabel(variation, t)}>
        {isFallback && (
          <ABTestPill placement={'version'} status={EEmailABTestStatus.ResolvedFallback} />
        )}
      </Card.Header>
      <Card.Content contentClassName={styles.content}>
        <List className={styles.metrics}>
          {AB_TEST_METRICS.map((metric) => (
            <List.Item className={styles.metric} key={metric}>
              <span className={styles.metricLabel}>
                <Text.DefaultRegular>{getABTestMetricLabel(metric, t)}</Text.DefaultRegular>
                {metricPillStatus && metric === abTest.winning_metric && (
                  <ABTestPill placement={'metric'} status={metricPillStatus} />
                )}
              </span>
              <Text.DefaultMedium>{formatABTestRate(summary.rates[metric])}</Text.DefaultMedium>
            </List.Item>
          ))}
        </List>
      </Card.Content>
    </Card>
  );
}

export default ABTestSummaryVersion;
